import * as Sentry from "@sentry/nextjs";
import { SeverityLevel } from "@sentry/types";
import { HEADER_NAME_MPT } from "@lmn/nextjs-appfw/dist/common";
import { EnvConfig } from "@/lib/Helpers/getServerConfig";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";

const isResponseObject = (error: any): error is Response => {
  if (error instanceof Response) {
    return true;
  } else {
    return false;
  }
};

const getInfoFromResponse = async (
  res: Response,
): Promise<string[][] | null> => {
  const contentTypeHeader = res.headers.get("content-type");
  if (contentTypeHeader?.includes("text/plain")) {
    try {
      const errorMessage = await res.text();
      return [["errorMessage", errorMessage]];
    } catch (err) {
      console.error("Error parsing response:", err);
      return null;
    }
  }
  try {
    const parsedResponse = await res.json();
    const tags: string[][] = [];
    if (!parsedResponse) {
      return null;
    }
    const { uuid, messages } = parsedResponse;
    tags.push(["uuid", uuid]);

    if (messages && messages instanceof Array && messages.length > 0) {
      tags.push(["errorHint", messages[0]]);
    }
    return tags;
  } catch (err) {
    console.error("Error parsing response:", err);
    return null;
  }
};

type FetchDataOptions = {
  url: string;
  method?: "GET" | "POST";
  params?: Record<string, any> | null;
  headers?: Record<string, string>;
  rawData?: boolean;
  next?: { revalidate: number };
  cache?: RequestCache;
  mpt?: string;
  transactionId?: string;
  searchId?: string;
  service?: string;
  envConfig: EnvConfig;
  timeout?: number; // milliseconds, default 30000
};

export default async function fetchData({
  url,
  method = "GET",
  params = null,
  headers: customHeaders = {},
  rawData = false,
  next,
  cache,
  mpt,
  transactionId,
  searchId,
  service,
  envConfig,
  timeout = 30000,
}: FetchDataOptions) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const options: RequestInit = {
    method,
    signal: controller.signal,
    headers: {
      "Content-Type": "application/json",
      ...customHeaders,
      ...(mpt ? { [HEADER_NAME_MPT]: mpt } : {}),
    },
  };

  if (next) {
    options.next = next;
  }
  if (cache) {
    options.cache = cache;
  } else if (!next) {
    // No explicit caching strategy — opt out of Next.js disk fetch cache.
    // Without this, Next.js caches all fetches to disk by default, causing
    // unbounded growth since most fetches include unique per-user params.
    options.cache = "no-store";
  }

  const isProduction = envConfig.currentEnv === "production";

  // Performance monitoring setup
  const startTime = performance.now();
  let finalUrl = url;
  let hasLoggedMetric = false;

  if (method === "GET" && params) {
    const queryParams = new URLSearchParams(params).toString();
    finalUrl += "?" + queryParams;
  } else if (method === "POST" && params) {
    options.body = JSON.stringify(params);
  }

  try {
    const response = await fetch(finalUrl, options);
    clearTimeout(timeoutId);

    // Log performance metrics
    const endTime = performance.now();
    const duration = endTime - startTime;

    sendKibanaLog({
      envConfig,
      transactionId,
      searchId,
      message: "LOG METRIC ACTION",
      component: "metrics",
      category: "metrics",
      jsonMessage: {
        platformName: "ICARUS",
        service: service || "unknown",
        method: method,
        duration: Math.round(duration),
        statusCode: response.status,
        url: response.url || finalUrl,
      },
    });
    hasLoggedMetric = true;

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    if (rawData) {
      return await response.text();
    }

    const responseText = await response.text();

    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      console.warn("[Fetch Diagnostics]", {
        event: "invalid_json_response",
        service: service || "unknown",
        method,
        status: response.status,
        url: response.url || finalUrl,
        contentType: response.headers.get("content-type"),
        contentLength: response.headers.get("content-length"),
        bodyPreview: responseText.slice(0, 200),
        transactionId,
        searchId,
      });
      throw parseError;
    }
  } catch (error) {
    clearTimeout(timeoutId);

    const endTime = performance.now();
    const duration = endTime - startTime;
    const isAbortError =
      error instanceof Error && error.name === "AbortError";

    if (isAbortError) {
      console.warn("[Fetch Diagnostics]", {
        event: "request_timeout",
        service: service || "unknown",
        method,
        duration: Math.round(duration),
        url: finalUrl,
        transactionId,
        searchId,
      });
    }

    if (!isAbortError && !hasLoggedMetric) {
      sendKibanaLog({
        envConfig,
        transactionId,
        searchId,
        message: "LOG METRIC ACTION",
        component: "metrics",
        jsonMessage: {
          platformName: "ICARUS",
          service: service || "unknown",
          method: method,
          duration: Math.round(duration),
          statusCode:
            error instanceof Error && "status" in error
              ? (error as any).status
              : 0,
          errorName: error instanceof Error ? error.name : "Unknown",
          errorMessage: error instanceof Error ? error.message : String(error),
          url: finalUrl,
        },
      });
    }

    const tags = [
      ["module", "api"],
      ["service", service],
    ];
    const errorMessage = `API call: failed fetching ${service}`;
    let severityLevel: SeverityLevel = "warning";

    if (isResponseObject(error)) {
      const moreTags = await getInfoFromResponse(error);
      if (moreTags) {
        tags.push(...moreTags);
      }
      severityLevel = "fatal";
    }

    if (isProduction) {
      Sentry.withScope((scope) => {
        tags.forEach((tag) => {
          if (tag[0] !== undefined) scope.setTag(tag[0], tag[1]);
        });
        scope.setLevel(severityLevel);
        Sentry.captureException(error instanceof Error ? error : errorMessage);
      });
    }

    console.error("Request error:", error);

    return {
      apiError: true,
      message: error instanceof Error ? error.message : errorMessage,
      name: error instanceof Error ? error.name : "FetchError",
      stack: error instanceof Error ? error.stack : undefined,
      url: finalUrl,
      method: method,
    };
  }
}
